// pages/Agents/AgentSpace/AgentSpaceConsole.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, CircularProgress, Button, IconButton, Chip,
    TextField, InputAdornment, MenuItem, Select, FormControl,
    Collapse, Skeleton, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import TerminalIcon from '@mui/icons-material/Terminal';
import BoltIcon from '@mui/icons-material/Bolt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import EmailIcon from '@mui/icons-material/Email';
import PollIcon from '@mui/icons-material/Poll';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChatIcon from '@mui/icons-material/Chat';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import axiosInstance from '../../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const monoFont = "'Fira Code', 'Cascadia Code', 'JetBrains Mono', monospace";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#8b5cf6',
    success: '#059669',
    danger: '#dc2626',
    warning: '#f59e0b',
    consoleBg: '#0f172a',
    consoleText: '#e2e8f0',
    consoleDim: '#64748b',
    consoleGreen: '#4ade80',
    consoleRed: '#f87171',
    consoleYellow: '#fbbf24',
    consolePurple: '#c084fc',
    consoleCyan: '#22d3ee',
};

const AGENT_COLORS = {
    survey: '#60a5fa', crm: '#fbbf24', chat_history: '#818cf8',
    events: '#34d399', gmail: '#f87171', outlook: '#60a5fa',
    'gmail-calendar': '#22d3ee', gmail_cleanup: '#c084fc',
};

const AGENT_ICONS_SMALL = {
    survey: <PollIcon sx={{ fontSize: 13 }} />,
    crm: <PeopleIcon sx={{ fontSize: 13 }} />,
    chat_history: <ChatIcon sx={{ fontSize: 13 }} />,
    events: <EventIcon sx={{ fontSize: 13 }} />,
    gmail: <MailOutlineIcon sx={{ fontSize: 13 }} />,
    outlook: <EmailIcon sx={{ fontSize: 13 }} />,
    'gmail-calendar': <CalendarMonthIcon sx={{ fontSize: 13 }} />,
    gmail_cleanup: <CleaningServicesIcon sx={{ fontSize: 13 }} />,
};

const AGENT_LABELS = {
    survey: 'AI Survey', crm: 'AI CRM', chat_history: 'AI Chat History',
    events: 'AI Events', gmail: 'AI Gmail', outlook: 'AI Outlook',
    'gmail-calendar': 'AI Calendar', gmail_cleanup: 'AI Gmail Cleanup',
};

function formatTimestamp(isoString) {
    if (!isoString) return '';
    const d = new Date(isoString);
    return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatDate(isoString) {
    if (!isoString) return '';
    const d = new Date(isoString);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return 'Today';
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDuration(ms) {
    if (!ms) return '0ms';
    if (ms < 1000) return `${ms}ms`;
    const s = (ms / 1000).toFixed(1);
    return `${s}s`;
}

function ConsoleLogEntry({ log, expanded, onToggle }) {
    const isCompleted = log.status === 'completed';
    const isFailed = log.status === 'failed';
    const statusColor = isCompleted ? colors.consoleGreen : isFailed ? colors.consoleRed : colors.consoleYellow;
    const agentColor = AGENT_COLORS[log.agent_type] || colors.consolePurple;
    const hasOutput = log.output && (typeof log.output === 'object' ? Object.keys(log.output).length > 0 : !!log.output);
    const hasComment = !!log.agent_comment;
    const canExpand = hasOutput || hasComment;

    return (
        <Box sx={{
            py: 0.5, px: 1.5,
            borderLeft: `2px solid ${statusColor}40`,
            transition: 'all 0.15s',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.03)', borderLeftColor: statusColor },
        }}>
            <Box
                onClick={canExpand ? onToggle : undefined}
                sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 1,
                    cursor: canExpand ? 'pointer' : 'default',
                }}
            >
                {/* Timestamp */}
                <Typography sx={{
                    fontFamily: monoFont, fontSize: '0.65rem', color: colors.consoleDim,
                    minWidth: 65, flexShrink: 0, pt: 0.15,
                }}>
                    {formatTimestamp(log.timestamp)}
                </Typography>

                {/* Status icon */}
                <Box sx={{ flexShrink: 0, pt: 0.15 }}>
                    {isCompleted ? (
                        <CheckCircleIcon sx={{ fontSize: 13, color: colors.consoleGreen }} />
                    ) : isFailed ? (
                        <ErrorIcon sx={{ fontSize: 13, color: colors.consoleRed }} />
                    ) : (
                        <AccessTimeIcon sx={{ fontSize: 13, color: colors.consoleYellow }} />
                    )}
                </Box>

                {/* Agent badge */}
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.3,
                    px: 0.5, py: 0.1, borderRadius: '4px',
                    bgcolor: `${agentColor}18`, color: agentColor,
                    flexShrink: 0,
                }}>
                    {AGENT_ICONS_SMALL[log.agent_type] || <AutoAwesomeIcon sx={{ fontSize: 13 }} />}
                    <Typography sx={{ fontFamily: monoFont, fontSize: '0.6rem', fontWeight: 600 }}>
                        {AGENT_LABELS[log.agent_type] || log.agent_type}
                    </Typography>
                </Box>

                {/* Action + Task name */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Action type line */}
                    <Typography sx={{
                        fontFamily: monoFont, fontSize: '0.7rem', color: colors.consoleText,
                        display: 'flex', alignItems: 'center', gap: 0.5,
                    }}>
                        <Typography component="span" sx={{ fontFamily: monoFont, fontSize: '0.65rem', color: colors.consoleCyan }}>
                            {log.action_type}
                        </Typography>
                        <Typography component="span" sx={{ fontFamily: monoFont, fontSize: '0.65rem', color: colors.consoleDim }}>
                            {log.label && log.label !== log.action_type ? `\u2014 ${log.label}` : ''}
                        </Typography>
                    </Typography>

                    {/* Task name */}
                    {log.task_name && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.15 }}>
                            <Typography component="span" sx={{ fontFamily: monoFont, fontSize: '0.55rem', color: colors.consoleYellow, fontWeight: 600 }}>
                                TASK
                            </Typography>
                            <Typography sx={{ fontFamily: monoFont, fontSize: '0.58rem', color: colors.consoleDim }} noWrap>
                                {log.task_name}
                            </Typography>
                        </Box>
                    )}

                    {/* Agent comment preview (collapsed â€” single line) */}
                    {hasComment && !expanded && (
                        <Typography sx={{
                            fontFamily: monoFont, fontSize: '0.58rem', color: 'rgba(192,132,252,0.7)',
                            mt: 0.15, fontStyle: 'italic',
                        }} noWrap>
                            {log.agent_comment}
                        </Typography>
                    )}

                    {/* Error message */}
                    {isFailed && log.error_message && (
                        <Typography sx={{ fontFamily: monoFont, fontSize: '0.6rem', color: colors.consoleRed, mt: 0.25 }}>
                            Error: {log.error_message}
                        </Typography>
                    )}
                </Box>

                {/* Duration + expand */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    <Typography sx={{ fontFamily: monoFont, fontSize: '0.6rem', color: colors.consoleDim }}>
                        {formatDuration(log.duration_ms)}
                    </Typography>
                    {canExpand && (
                        expanded
                            ? <ExpandLessIcon sx={{ fontSize: 14, color: colors.consoleDim }} />
                            : <ExpandMoreIcon sx={{ fontSize: 14, color: colors.consoleDim }} />
                    )}
                </Box>
            </Box>

            {/* Expanded section: agent comment + output */}
            <Collapse in={expanded}>
                <Box sx={{ ml: '76px', mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {/* Agent comment (full) */}
                    {log.agent_comment_full && (
                        <Box sx={{
                            p: 1, bgcolor: 'rgba(139,92,246,0.08)',
                            borderRadius: '6px', border: '1px solid rgba(139,92,246,0.15)',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <AutoAwesomeIcon sx={{ fontSize: 11, color: colors.consolePurple }} />
                                <Typography sx={{ fontFamily: monoFont, fontSize: '0.55rem', fontWeight: 700, color: colors.consolePurple, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Agent Response
                                </Typography>
                            </Box>
                            <Typography sx={{
                                fontFamily, fontSize: '0.68rem', color: 'rgba(226,232,240,0.85)',
                                lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                            }}>
                                {log.agent_comment_full}
                            </Typography>
                        </Box>
                    )}

                    {/* Action output JSON */}
                    {hasOutput && (
                        <Box sx={{
                            p: 1, bgcolor: 'rgba(0,0,0,0.3)',
                            borderRadius: '6px', maxHeight: 220, overflow: 'auto',
                            border: '1px solid rgba(255,255,255,0.06)',
                            '&::-webkit-scrollbar': { width: '4px' },
                            '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.1)', borderRadius: '4px' },
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <TerminalIcon sx={{ fontSize: 11, color: colors.consoleCyan }} />
                                <Typography sx={{ fontFamily: monoFont, fontSize: '0.55rem', fontWeight: 700, color: colors.consoleCyan, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Output
                                </Typography>
                            </Box>
                            <Typography component="pre" sx={{
                                fontFamily: monoFont, fontSize: '0.6rem', color: colors.consoleText,
                                whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0, lineHeight: 1.5,
                            }}>
                                {typeof log.output === 'string' ? log.output : JSON.stringify(log.output, null, 2)}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
}

export default function AgentSpaceConsole({ chatbotId }) {
    const [logs, setLogs] = useState([]);
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [expandedLogs, setExpandedLogs] = useState({});

    // Filters
    const [agentFilter, setAgentFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [timeRange, setTimeRange] = useState('all');
    const [search, setSearch] = useState('');
    const [searchDebounced, setSearchDebounced] = useState('');

    const scrollRef = useRef(null);
    const searchTimer = useRef(null);

    // Debounce search
    const handleSearchChange = (val) => {
        setSearch(val);
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => setSearchDebounced(val), 400);
    };

    const fetchLogs = useCallback(async (pageNum = 1, append = false) => {
        if (!chatbotId) return;
        if (pageNum === 1) setLoading(true);
        else setLoadingMore(true);

        try {
            const params = {
                chatbot_id: chatbotId,
                page: pageNum,
                per_page: 50,
            };
            if (agentFilter) params.agent_type = agentFilter;
            if (statusFilter) params.status = statusFilter;
            if (timeRange !== 'all') params.time_range = timeRange;
            if (searchDebounced) params.search = searchDebounced;

            const resp = await axiosInstance.get('/api/agent-space/console/logs', { params });
            if (resp.data.success) {
                const data = resp.data.data;
                setLogs(prev => append ? [...prev, ...data.logs] : data.logs);
                setHasMore(data.has_more);
                setPage(data.page);
                if (data.metrics) setMetrics(data.metrics);
            }
        } catch (err) {
            console.error('Failed to fetch console logs:', err);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [chatbotId, agentFilter, statusFilter, timeRange, searchDebounced]);

    useEffect(() => {
        setLogs([]);
        setPage(1);
        fetchLogs(1, false);
    }, [fetchLogs]);

    const handleRefresh = () => {
        setLogs([]);
        setPage(1);
        fetchLogs(1, false);
    };

    const handleLoadMore = () => {
        fetchLogs(page + 1, true);
    };

    const toggleLogExpand = (idx) => {
        setExpandedLogs(prev => ({ ...prev, [idx]: !prev[idx] }));
    };

    // Group logs by date
    const groupedLogs = [];
    let currentDate = null;
    logs.forEach((log, idx) => {
        const dateLabel = formatDate(log.timestamp);
        if (dateLabel !== currentDate) {
            currentDate = dateLabel;
            groupedLogs.push({ type: 'date', label: dateLabel });
        }
        groupedLogs.push({ type: 'log', log, idx });
    });

    const selectSx = {
        fontFamily: monoFont, fontSize: '0.65rem', color: colors.consoleText,
        bgcolor: 'rgba(255,255,255,0.05)', borderRadius: '6px',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
        '& .MuiSelect-icon': { color: colors.consoleDim },
        height: 32,
    };

    return (
        <Box sx={{
            height: '100%', display: 'flex', flexDirection: 'column',
            bgcolor: colors.consoleBg, borderRadius: '16px', overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.06)',
        }}>
            {/* Console Header */}
            <Box sx={{
                px: 2, py: 1.25,
                borderBottom: '1px solid rgba(255,255,255,0.06)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                bgcolor: 'rgba(255,255,255,0.02)',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TerminalIcon sx={{ fontSize: 18, color: colors.consoleGreen }} />
                    <Typography sx={{ fontFamily: monoFont, fontSize: '0.75rem', fontWeight: 700, color: colors.consoleText }}>
                        Agent Console
                    </Typography>
                    {metrics && (
                        <Typography sx={{ fontFamily: monoFont, fontSize: '0.6rem', color: colors.consoleDim }}>
                            {metrics.total_actions} actions
                        </Typography>
                    )}
                </Box>
                <Tooltip title="Refresh">
                    <IconButton size="small" onClick={handleRefresh} sx={{ color: colors.consoleDim, '&:hover': { color: colors.consoleGreen } }}>
                        <RefreshIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Metrics Bar */}
            {metrics && metrics.total_actions > 0 && (
                <Box sx={{
                    display: 'flex', gap: 1, px: 2, py: 1,
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.4, borderRadius: '6px', bgcolor: 'rgba(139,92,246,0.1)' }}>
                        <ForumOutlinedIcon sx={{ fontSize: 12, color: colors.consolePurple }} />
                        <Typography sx={{ fontFamily: monoFont, fontSize: '0.6rem', color: colors.consolePurple }}>
                            {metrics.total_conversations} sessions
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.4, borderRadius: '6px', bgcolor: 'rgba(34,211,238,0.1)' }}>
                        <BoltIcon sx={{ fontSize: 12, color: colors.consoleCyan }} />
                        <Typography sx={{ fontFamily: monoFont, fontSize: '0.6rem', color: colors.consoleCyan }}>
                            {metrics.total_actions} actions
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.4, borderRadius: '6px', bgcolor: 'rgba(74,222,128,0.1)' }}>
                        <CheckCircleIcon sx={{ fontSize: 12, color: colors.consoleGreen }} />
                        <Typography sx={{ fontFamily: monoFont, fontSize: '0.6rem', color: colors.consoleGreen }}>
                            {metrics.completed_actions} passed
                        </Typography>
                    </Box>
                    {metrics.failed_actions > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.4, borderRadius: '6px', bgcolor: 'rgba(248,113,113,0.1)' }}>
                            <ErrorIcon sx={{ fontSize: 12, color: colors.consoleRed }} />
                            <Typography sx={{ fontFamily: monoFont, fontSize: '0.6rem', color: colors.consoleRed }}>
                                {metrics.failed_actions} failed
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.4, borderRadius: '6px', bgcolor: 'rgba(251,191,36,0.1)' }}>
                        <TrendingUpIcon sx={{ fontSize: 12, color: colors.consoleYellow }} />
                        <Typography sx={{ fontFamily: monoFont, fontSize: '0.6rem', color: colors.consoleYellow }}>
                            {metrics.success_rate}% success
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Filters Bar */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
                borderBottom: '1px solid rgba(255,255,255,0.04)',
                flexWrap: 'wrap',
            }}>
                {/* Search */}
                <TextField
                    size="small"
                    placeholder="Search logs..."
                    value={search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 14, color: colors.consoleDim }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        flex: 1, minWidth: 150,
                        '& .MuiOutlinedInput-root': {
                            fontFamily: monoFont, fontSize: '0.65rem', color: colors.consoleText,
                            bgcolor: 'rgba(255,255,255,0.05)', borderRadius: '6px', height: 32,
                            '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                            '&.Mui-focused fieldset': { borderColor: colors.accent },
                        },
                        '& input::placeholder': { color: colors.consoleDim, opacity: 1 },
                    }}
                />

                {/* Agent filter */}
                <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                        value={agentFilter}
                        onChange={(e) => setAgentFilter(e.target.value)}
                        displayEmpty
                        sx={selectSx}
                        MenuProps={{ PaperProps: { sx: { bgcolor: '#1e293b', color: colors.consoleText, '& .MuiMenuItem-root': { fontFamily: monoFont, fontSize: '0.65rem' } } } }}
                    >
                        <MenuItem value="">All Agents</MenuItem>
                        {Object.entries(AGENT_LABELS).map(([key, label]) => (
                            <MenuItem key={key} value={key}>{label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Status filter */}
                <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        displayEmpty
                        sx={selectSx}
                        MenuProps={{ PaperProps: { sx: { bgcolor: '#1e293b', color: colors.consoleText, '& .MuiMenuItem-root': { fontFamily: monoFont, fontSize: '0.65rem' } } } }}
                    >
                        <MenuItem value="">All Status</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                    </Select>
                </FormControl>

                {/* Time range */}
                <FormControl size="small" sx={{ minWidth: 110 }}>
                    <Select
                        value={timeRange}
                        onChange={(e) => setTimeRange(e.target.value)}
                        sx={selectSx}
                        MenuProps={{ PaperProps: { sx: { bgcolor: '#1e293b', color: colors.consoleText, '& .MuiMenuItem-root': { fontFamily: monoFont, fontSize: '0.65rem' } } } }}
                    >
                        <MenuItem value="all">All Time</MenuItem>
                        <MenuItem value="last_hour">Last Hour</MenuItem>
                        <MenuItem value="last_24h">Last 24h</MenuItem>
                        <MenuItem value="last_7d">Last 7 Days</MenuItem>
                        <MenuItem value="last_30d">Last 30 Days</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* Log Stream */}
            <Box
                ref={scrollRef}
                sx={{
                    flex: 1, overflow: 'auto', py: 0.5,
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: '3px' },
                }}
            >
                {loading ? (
                    <Box sx={{ px: 2, py: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {[...Array(12)].map((_, i) => (
                            <Skeleton key={i} variant="rounded" height={24} sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: '4px' }} />
                        ))}
                    </Box>
                ) : logs.length === 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1.5 }}>
                        <TerminalIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.1)' }} />
                        <Typography sx={{ fontFamily: monoFont, fontSize: '0.8rem', color: colors.consoleDim }}>
                            No agent activity logs yet
                        </Typography>
                        <Typography sx={{ fontFamily: monoFont, fontSize: '0.65rem', color: 'rgba(255,255,255,0.15)', textAlign: 'center', maxWidth: 300 }}>
                            Agent actions will appear here as a real-time log stream when conversations execute tool calls
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {groupedLogs.map((item, i) => {
                            if (item.type === 'date') {
                                return (
                                    <Box key={`date-${i}`} sx={{
                                        display: 'flex', alignItems: 'center', gap: 1,
                                        px: 2, py: 0.5, mt: i > 0 ? 0.5 : 0,
                                    }}>
                                        <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.06)' }} />
                                        <Typography sx={{ fontFamily: monoFont, fontSize: '0.58rem', color: colors.consoleDim, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            {item.label}
                                        </Typography>
                                        <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.06)' }} />
                                    </Box>
                                );
                            }
                            return (
                                <ConsoleLogEntry
                                    key={item.idx}
                                    log={item.log}
                                    expanded={!!expandedLogs[item.idx]}
                                    onToggle={() => toggleLogExpand(item.idx)}
                                />
                            );
                        })}

                        {/* Load More */}
                        {hasMore && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                                <Button
                                    onClick={handleLoadMore}
                                    disabled={loadingMore}
                                    startIcon={loadingMore ? <CircularProgress size={12} sx={{ color: colors.consoleDim }} /> : <KeyboardArrowDownIcon />}
                                    sx={{
                                        fontFamily: monoFont, fontSize: '0.65rem', fontWeight: 600,
                                        textTransform: 'none', color: colors.consoleDim, borderRadius: '8px', px: 2,
                                        border: '1px solid rgba(255,255,255,0.08)',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.15)' },
                                    }}
                                >
                                    {loadingMore ? 'Loading...' : 'Load More'}
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
}
